import React, { createContext, useContext, useState, useEffect } from "react";
import { User, UserData } from "../types/user";
import { tokenStore } from "../services/tokenStore";

const API_BASE_URL =
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? "https://apicashboard.com"
    : ""; // In dev, requests go to same origin and are proxied by webpack to localhost:8000

export type AuthState = "unauthenticated" | "loading" | "authenticated" | "expired";

interface AuthContextType {
  user: User | null;
  authState: AuthState;
  login: () => void;
  logout: () => void;
  markExpired: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

function getAuthHeader(): Record<string, string> {
  const token = tokenStore.getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authState, setAuthState] = useState<AuthState>("loading");
  const [, setActiveDialog] = useState<Office.Dialog | null>(null);

  const refreshAccessToken = async (): Promise<boolean> => {
    try {
      const body: Record<string, string> = {};
      const rt = tokenStore.getRefreshToken();
      if (rt) body.refresh_token = rt;

      const res = await fetch(`${API_BASE_URL}/api/excel/auth/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.access_token && data.refresh_token) {
          tokenStore.setTokens(data.access_token, data.refresh_token);
        }
        return true;
      }
    } catch {
      // Network error or server unreachable
    }
    return false;
  };

  const fetchUser = async (): Promise<User | null> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/excel/auth/me/`, {
        credentials: "include",
        headers: getAuthHeader(),
      });
      if (res.ok) {
        const userData: UserData = await res.json();
        return new User(userData);
      }
    } catch {
      // Network error or server unreachable
    }
    return null;
  };

  // Initial auth check + periodic refresh
  useEffect(() => {
    const initAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const authError = urlParams.get("auth_error");

      // Clean up URL params if present
      if (window.location.search) {
        if (window.history && typeof window.history.replaceState === "function") {
          const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
        }
      }

      if (authError) {
        setAuthState("unauthenticated");
        return;
      }

      let fetchedUser = await fetchUser();
      if (!fetchedUser) {
        const refreshed = await refreshAccessToken();
        if (refreshed) fetchedUser = await fetchUser();
      }

      if (fetchedUser) {
        setUser(fetchedUser);
        setAuthState("authenticated");
      } else {
        // Only set unauthenticated if login hasn't already completed while we were fetching
        setAuthState((current) => (current === "loading" ? "unauthenticated" : current));
      }
    };

    initAuth();

    // Refresh token every 15 minutes
    const refreshInterval = setInterval(async () => {
      await refreshAccessToken();
    }, 15 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  const login = () => {
    // Consume the force-login flag set by logout() so the user must re-enter
    // credentials instead of being auto-logged-in via a live PropelAuth session.
    let forceLogin = false;
    try {
      forceLogin = localStorage.getItem("cb_force_login") === "1";
      if (forceLogin) localStorage.removeItem("cb_force_login");
    } catch { /* storage blocked */ }

    const loginPath = `/login-redirect.html${forceLogin ? "?force_login=1" : ""}`;

    if (Office.context.platform === Office.PlatformType.OfficeOnline) {
      // In Excel Online, displayDialogAsync renders the dialog as an iframe.
      // PropelAuth's frame-ancestors CSP blocks auth.cashboard.finance from
      // loading in any iframe. window.open() creates a true browser popup
      // (not an iframe) so the CSP restriction doesn't apply.
      //
      // PropelAuth also sets COOP:same-origin on its login pages, severing
      // window.opener permanently. Chrome additionally partitions localStorage
      // by top-level site, so the popup can't communicate back via storage or
      // postMessage. We use server-side polling instead:
      //   1. Fetch oauth_login to get auth_url + poll_code before opening popup
      //   2. Pass auth_url to the popup via URL param (avoids a second fetch)
      //   3. auth-callback.html calls oauth_exchange, which deposits tokens in
      //      the DB under the poll_code (= state UUID)
      //   4. Task pane polls oauth_redeem every 1.5 s until tokens appear
      fetch(`${API_BASE_URL}/api/excel/auth/login/?format=json`)
        .then((res) => res.json())
        .then((data: { auth_url: string; poll_code: string }) => {
          let authUrl = data.auth_url;
          const pollCode = data.poll_code;
          if (forceLogin) {
            authUrl += (authUrl.includes("?") ? "&" : "?") + "prompt=login";
          }

          const w = 540, h = 600;
          const popup = window.open(
            `${window.location.origin}/login-redirect.html?auth_url=${encodeURIComponent(authUrl)}`,
            "cashboard_auth",
            `popup,width=${w},height=${h},left=${Math.round((screen.width - w) / 2)},top=${Math.round((screen.height - h) / 2)}`
          );

          if (!popup) {
            // Popup blocked — fall back to full-page redirect flow
            try { localStorage.setItem("auth_mode", "redirect"); } catch { /* storage blocked */ }
            window.location.href = loginPath;
            return;
          }

          let attempts = 0;
          const maxAttempts = 120; // 120 × 1.5 s = 3 minutes
          const pollInterval = setInterval(async () => {
            attempts++;
            if (attempts > maxAttempts) {
              clearInterval(pollInterval);
              setAuthState("unauthenticated");
              return;
            }
            try {
              const res = await fetch(
                `${API_BASE_URL}/api/excel/auth/redeem/?code=${encodeURIComponent(pollCode)}`
              );
              if (res.ok) {
                const tokenData = await res.json();
                if (tokenData.ready && tokenData.access_token && tokenData.refresh_token) {
                  clearInterval(pollInterval);
                  tokenStore.setTokens(tokenData.access_token, tokenData.refresh_token);
                  setAuthState("authenticated");
                  fetchUser().then((u) => { if (u) setUser(u); });
                }
              }
            } catch { /* network error — keep polling */ }
          }, 1500);
        })
        .catch(() => { setAuthState("unauthenticated"); });
      return;
    }

    const dialogUrl = `${window.location.origin}${loginPath}`;
    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 80, width: Math.min(99, Math.ceil((540 / window.screen.width) * 100)), promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) return;
        const dialog = asyncResult.value;
        setActiveDialog(dialog);

        let authSuccessReceived = false;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg: { message: string; origin: string | undefined } | { error: number }) => {
            if (!("message" in arg)) return;
            try {
              const message = JSON.parse(arg.message);
              if (message.type === "auth_success" && !authSuccessReceived) {
                authSuccessReceived = true;
                try { dialog.close(); } catch { /* already closed */ }
                setActiveDialog(null);

                if (message.access_token && message.refresh_token) {
                  tokenStore.setTokens(message.access_token, message.refresh_token);
                }

                setAuthState("authenticated");
                fetchUser().then((u) => {
                  if (u) {
                    setUser(u);
                  }
                });
              } else if (message.type === "auth_error" && !authSuccessReceived) {
                try { dialog.close(); } catch { /* already closed */ }
                setActiveDialog(null);
                setAuthState("unauthenticated");
              }
            } catch {
              // ignore non-JSON messages
            }
          }
        );
      }
    );
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/excel/auth/logout/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        credentials: "include",
      });
    } catch {
      // Best effort
    }
    // Flag so the next login() call forces PropelAuth to show credentials
    // instead of auto-signing in from the still-active PropelAuth session.
    try { localStorage.setItem("cb_force_login", "1"); } catch { /* storage blocked */ }
    tokenStore.clearTokens();
    setUser(null);
    setAuthState("unauthenticated");
  };

  const markExpired = () => {
    setAuthState("expired");
  };

  return (
    <AuthContext.Provider value={{ user, authState, login, logout, markExpired }}>
      {children}
    </AuthContext.Provider>
  );
};
